/**
 * <ReadOnlyShareBuilder> — Builder route's read-only-share render branch.
 *
 * `external-sharing` Phase 4 Session 4.2 (2026-05-09).
 *
 * Mounted by `<BuilderPage>` when the URL carries `?share=<id>`. Sources
 * data from the public sharing endpoints (`/api/shared/:id/deal` +
 * `/api/shared/:id/model-view`) — no auth-mode `useConversation` chain.
 *
 * Layout (top → bottom):
 *   1. Tab bar (3 tabs — Workbench, Signal, Model)
 *   2. Read-only toolbar (model name + version + READ-ONLY badge)
 *   3. <SharedDealBanner> with the [Fork] CTA
 *   4. <HeroMetricsHeader> (Signal + Model tabs only — derived from
 *       cellValues since there's no live engine)
 *   5. Tab body:
 *      - Workbench → "Fork to interact" empty state
 *      - Signal    → variant component or placeholder
 *      - Model     → <ModelLayout readOnly> or <LayoutUnavailablePanel>
 *
 * Outer chrome (`<Sidebar>` + `<Header>` + `<HeaderShareBell>`) is owned
 * by `__root.tsx` and renders normally — recipient is authenticated.
 *
 * `<ConversationSidebar>` is intentionally NOT rendered here; the
 * recipient is viewing a foreign deal and doesn't need their own
 * conversation list cluttering the read-only view. Global sidebar nav
 * still works for navigating away.
 */
import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import type { ModelSchema } from '@arcanum-hq/shared-types';
import { ApiError, type ModelViewPayload, type ModelViewResponse } from '@/lib/api';
import { usePublicDeal, useSharedModelView } from '@/hooks/use-public-deal';
import { useModel } from '@/hooks/use-models-v2';
import { useForkShare } from '@/hooks/use-shares';
import { useViewerOverrides } from '@/hooks/use-viewer-overrides';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { NarrativeBar } from '@/components/layout/NarrativeBar';
import { SandboxBanner } from '@/components/deal';
import { SharedDealBanner } from './SharedDealBanner';
import { HeroMetricsHeader } from './HeroMetricsHeader';
import {
  ModelLayout,
  LayoutUnavailablePanel,
} from './model-view';
import { SignalOperatingAsset, SignalNewDevelopment } from './signal';
import { BUILDER_TABS, type BuilderTab } from '@/routes/builder';

const TAB_LABELS: Record<BuilderTab, string> = {
  workbench: 'Workbench',
  signal: 'Signal',
  model: 'Model',
};

export interface ReadOnlyShareBuilderProps {
  shareId: string;
  activeTab: BuilderTab;
}

/**
 * Walk the schema's outputs and look up each one's cell value, returning
 * a `Record<outputName, number>` that mirrors the live-streaming
 * `liveMetrics` shape `<HeroMetricsHeader>` + Signal variants consume.
 *
 * `cellValues` is keyed by `${sheet}!${A1}` (e.g. `Outputs!B12`); each
 * `OutputDefinition` carries `sheet` + `cell` (the A1 address). Numeric
 * cells map through; non-numeric (CellError objects, formula strings,
 * blanks) skip — the renderer's `Number.isFinite` guard already handles
 * the missing case.
 */
function deriveLiveMetricsFromCellValues(
  modelSchema: ModelSchema | null,
  cellValues: Record<string, unknown>,
): Record<string, number> {
  if (!modelSchema?.outputs) return {};
  const out: Record<string, number> = {};
  for (const def of modelSchema.outputs) {
    const key = `${def.sheet}!${def.cell}`;
    const value = cellValues[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      out[def.name] = value;
    }
  }
  return out;
}

export function ReadOnlyShareBuilder({
  shareId,
  activeTab,
}: ReadOnlyShareBuilderProps) {
  const navigate = useNavigate();
  const dealQuery = usePublicDeal(shareId);
  const modelViewQuery = useSharedModelView(shareId);
  const modelDetail = useModel(dealQuery.data?.modelId ?? null);
  const forkShareMutation = useForkShare();
  // `external-sharing` Phase 5 Session 5.4 (2026-05-09) — sandbox
  // overrides for the internal-recipient `?share=<id>` Model tab.
  // Same hook the public deal page (`<DealModelView>`) uses;
  // `<SandboxBanner shareMode='internal-recipient'>` surfaces the
  // Fork-to-persist CTA when the recipient stages edits.
  const sandbox = useViewerOverrides(shareId);
  const sandboxHasOverrides = Object.keys(sandbox.overrides).length > 0;

  const modelSchema: ModelSchema | null = modelDetail.data?.data ?? null;

  // deal-signal-overhaul Phase 4 Session 5 (2026-05-12) — the prior
  // `inputValues` useMemo was removed when `SignalBody`'s prop shape
  // collapsed onto `SignalRootProps`. The Signal-tab variant components
  // now fetch their own inputValues via `useSignalFieldResolver`'s
  // public-deal context (which itself uses `useSharedModelView`). The
  // `cellValues` derivation stays — `<HeroMetricsHeader>` on the Model
  // tab still consumes the projected `liveMetrics` map.
  const cellValues: Record<string, unknown> = useMemo(() => {
    const mv = modelViewQuery.data;
    if (!mv) return {};
    return mv.cellValues;
  }, [modelViewQuery.data]);

  const liveMetrics = useMemo(
    () => deriveLiveMetricsFromCellValues(modelSchema, cellValues),
    [modelSchema, cellValues],
  );

  const handleTabChange = (next: BuilderTab): void => {
    void navigate({
      to: '/builder',
      search: (prev) => ({ ...prev, tab: next }),
    });
  };

  // Loading / error gates
  if (
    dealQuery.error instanceof ApiError &&
    dealQuery.error.status === 404
  ) {
    return (
      <ShareUnavailable
        message="This share has been revoked or the link is invalid."
      />
    );
  }
  if (dealQuery.isLoading) {
    return (
      <div
        className="flex-1 flex flex-col gap-3 p-6"
        data-testid="read-only-share-loading"
      >
        <Skeleton variant="block" className="h-12" />
        <Skeleton variant="block" className="h-72" />
      </div>
    );
  }
  if (!dealQuery.data) {
    return (
      <ShareUnavailable message="Couldn't load this shared deal. Try again later." />
    );
  }

  const deal = dealQuery.data;
  const signalVariant = deal.signalVariant;
  const signalDisabled = signalVariant === null;

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden"
      data-testid="read-only-share-builder"
      data-share-id={shareId}
    >
      {/* Tab bar */}
      <div
        className="flex items-center gap-1 px-5 pt-3 border-b border-panel-border bg-bg shrink-0"
        role="tablist"
        aria-label="Shared deal view"
      >
        {BUILDER_TABS.map((t) => {
          const disabled = t === 'signal' && signalDisabled;
          const tooltip = disabled
            ? "This model doesn't have a Signal view configured. Contact admin."
            : undefined;
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={activeTab === t}
              disabled={disabled}
              title={tooltip}
              onClick={() => handleTabChange(t)}
              data-testid={`builder-tab-${t}`}
              className={clsx(
                'px-4 py-1.5 text-sm font-mono cursor-pointer transition-colors -mb-px border-b-2 bg-transparent',
                activeTab === t
                  ? 'text-accent border-accent'
                  : 'text-text-muted hover:text-text border-transparent',
                disabled && 'opacity-40 cursor-not-allowed hover:text-text-muted',
              )}
            >
              {TAB_LABELS[t]}
            </button>
          );
        })}
      </div>

      {/* Read-only toolbar — model identity + READ-ONLY badge */}
      <div
        data-testid="read-only-toolbar"
        className="px-5 py-3.5 border-b border-panel-border flex items-center gap-5 shrink-0 bg-panel-bg"
      >
        <div className="shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-text">
              {modelSchema?.name ?? deal.modelId}
            </span>
            <span className="px-1.5 py-0.5 bg-accent-faint border border-accent/15 rounded-[2px] text-micro font-mono text-accent">
              v{deal.modelVersion}
            </span>
          </div>
        </div>
        <span className="text-xl text-text-bright font-body font-bold truncate max-w-64">
          {deal.title}
        </span>
        <span
          data-testid="read-only-badge"
          className="px-2 py-0.5 bg-warn-dim border border-warn/30 rounded-[2px] text-micro font-mono text-warn uppercase tracking-[0.5px]"
        >
          Read-only
        </span>
      </div>

      {/* SharedDealBanner — sender + Fork CTA */}
      <div className="px-5 pt-3 shrink-0">
        <SharedDealBanner
          shareId={shareId}
          ownerName={deal.ownerName ?? 'an Arcanum user'}
          dealTitle={deal.title}
        />
      </div>

      {/* deal-signal-overhaul Phase 4 Session 5 (2026-05-12) — the Signal
          tab now owns its own 5-cell hero strip via `<HeroReturns>` inside
          `<SignalOperatingAsset>` / `<SignalNewDevelopment>`. Keep the
          top-of-body `<HeroMetricsHeader>` on the Model tab only. */}
      {activeTab === 'model' && (
        <HeroMetricsHeader
          conversationId={deal.conversationId}
          modelSchema={modelSchema}
          liveMetrics={liveMetrics}
          metricHistory={null}
        />
      )}

      {/* Fork-error banner — surfaces 401/403/404/5xx from the mutation so
          "Fork to interact" never silently no-ops. */}
      {forkShareMutation.isError && (
        <div
          data-testid="fork-error-banner"
          className="border-l-2 border-danger/40 bg-danger-dim text-danger text-xs font-mono px-4 py-2 mx-4 mt-2 rounded-[2px]"
        >
          Fork failed: {forkShareMutation.error?.message || 'Unknown error'}.
          Try signing out and back in, or contact support if it persists.
        </div>
      )}

      {/* Tab body */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'workbench' ? (
          <WorkbenchPlaceholder
            shareId={shareId}
            isForking={forkShareMutation.isPending}
            onFork={() => forkShareMutation.mutate(shareId)}
          />
        ) : activeTab === 'signal' ? (
          <SignalBody
            signalVariant={signalVariant}
            shareId={shareId}
            conversationTitle={deal.title}
            modelId={deal.modelId}
            modelVersion={deal.modelVersion}
            aerialLatLng={deal.aerialLatLng ?? null}
          />
        ) : (
          <ModelBody
            modelViewState={modelViewQuery}
            shareId={shareId}
            sandbox={sandbox}
            hasOverrides={sandboxHasOverrides}
            onForkClick={() => forkShareMutation.mutate(shareId)}
          />
        )}
      </div>

      {/*
        NarrativeBar — `external-sharing` Phase 5 Session 5.2 (2026-05-09).

        Internal-recipient mounts only render the bar on Signal + Model
        tabs. The Workbench tab keeps its existing "Fork to interact"
        empty state (recipient sees a clear Fork CTA instead of a
        chat composer they can't use against a foreign deal). This
        matches the spec §10 contract: "the bar hides entirely on the
        Workbench tab in `'internal-recipient'` mode."

        `BuilderPage` early-returns to `<ReadOnlyShareBuilder>` when
        `?share=<id>` is set, so the auth-mode NarrativeBar mounts in
        `routes/builder.tsx:1587-1588` are unreachable in this branch —
        the bar lives here, scoped to the read-only-share flow. The
        gate is suppressed via `shareMode='internal-recipient'` (the
        recipient is the intended audience; no question ceiling).
      */}
      {(activeTab === 'signal' || activeTab === 'model') && (
        <NarrativeBar mode="prompt" shareMode="internal-recipient" />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function ShareUnavailable({ message }: { message: string }) {
  return (
    <div
      className="flex-1 flex items-center justify-center p-6"
      data-testid="share-unavailable"
    >
      <EmptyState title="SHARE UNAVAILABLE" description={message} />
    </div>
  );
}

interface WorkbenchPlaceholderProps {
  shareId: string;
  isForking: boolean;
  onFork: () => void;
}

function WorkbenchPlaceholder({
  shareId,
  isForking,
  onFork,
}: WorkbenchPlaceholderProps) {
  return (
    <div
      className="flex-1 flex items-center justify-center p-6"
      data-testid="read-only-workbench-placeholder"
      data-share-id={shareId}
    >
      <EmptyState
        title="READ-ONLY MODE"
        description="Fork this deal into your own conversation to ask the agent questions or persist edits."
        action={
          <Button
            variant="primary"
            onClick={onFork}
            disabled={isForking}
            data-testid="read-only-workbench-fork"
          >
            {isForking ? 'Forking…' : 'Fork to interact'}
          </Button>
        }
      />
    </div>
  );
}

interface SignalBodyProps {
  signalVariant: 'operating-asset' | 'new-development' | null;
  shareId: string;
  conversationTitle: string;
  modelId: string;
  modelVersion: string;
  /** Geocoded coordinates from `usePublicDeal`'s `aerialLatLng` projection. */
  aerialLatLng?: { lat: number; lng: number } | null;
}

// deal-signal-overhaul Phase 4 Session 5 (2026-05-12) — `<SignalBody>` was
// rewritten alongside the variant components. The auth recipient hits the
// `public-deal` resolver context (sourcing `inputValues` + `provenance` via
// `useSharedModelView`); pageSize stays at `4` to match the auth Builder
// Signal column width.
function SignalBody({
  signalVariant,
  shareId,
  conversationTitle,
  modelId,
  modelVersion,
  aerialLatLng,
}: SignalBodyProps) {
  const context = { kind: 'public-deal' as const, shareId };
  if (signalVariant === 'operating-asset') {
    return (
      <SignalOperatingAsset
        context={context}
        modelId={modelId}
        modelVersion={modelVersion}
        conversationTitle={conversationTitle}
        aerialLatLng={aerialLatLng ?? null}
        metricHistory={null}
        pageSize={4}
      />
    );
  }
  if (signalVariant === 'new-development') {
    return (
      <SignalNewDevelopment
        context={context}
        modelId={modelId}
        modelVersion={modelVersion}
        conversationTitle={conversationTitle}
        aerialLatLng={aerialLatLng ?? null}
        metricHistory={null}
        pageSize={4}
      />
    );
  }
  return (
    <div
      className="flex-1 flex items-center justify-center"
      data-testid="signal-body-placeholder"
    >
      <div className="text-center space-y-2 max-w-md font-mono text-sm">
        <div className="text-2xl text-accent">◆</div>
        <p className="text-text-bright">Signal view</p>
        <p className="text-text-muted">
          This model doesn&apos;t have a Signal variant configured.
        </p>
      </div>
    </div>
  );
}

interface ModelBodyProps {
  modelViewState: {
    data: ModelViewPayload | undefined;
    isLoading: boolean;
    error: Error | null;
  };
  shareId: string;
  sandbox: ReturnType<typeof useViewerOverrides>;
  hasOverrides: boolean;
  onForkClick: () => void;
}

function ModelBody({
  modelViewState,
  shareId,
  sandbox,
  hasOverrides,
  onForkClick,
}: ModelBodyProps) {
  if (modelViewState.isLoading) {
    return (
      <div className="p-6" data-testid="read-only-model-loading">
        <Skeleton variant="block" className="h-96" />
      </div>
    );
  }
  if (modelViewState.error) {
    return (
      <ShareUnavailable message="The shared model couldn't be loaded." />
    );
  }
  const data = modelViewState.data;
  if (!data) return null;

  if (data.layoutMissing) {
    return (
      <LayoutUnavailablePanel
        modelId={data.modelId}
        modelVersion={data.modelVersion}
        latestVersionWithLayout={data.latestVersionWithLayout}
        onCreateNew={() => {
          /* read-only: no-op (the recipient must fork first to create a new conversation) */
        }}
      />
    );
  }
  return (
    <ModelBodyHappyPath
      data={data}
      shareId={shareId}
      sandbox={sandbox}
      hasOverrides={hasOverrides}
      onForkClick={onForkClick}
    />
  );
}

interface ModelBodyHappyPathProps {
  data: ModelViewResponse;
  shareId: string;
  sandbox: ReturnType<typeof useViewerOverrides>;
  hasOverrides: boolean;
  onForkClick: () => void;
}

function ModelBodyHappyPath({
  data,
  shareId,
  sandbox,
  hasOverrides,
  onForkClick,
}: ModelBodyHappyPathProps) {
  // Consumer-side merge: overlay sandbox state on the base payload so the
  // grid reflects edits. TWO merges, both required:
  //   - `cellValues` ← `sandbox.recomputedValues` → formula/output cells
  //     (`resolveValue` reads `cellValues[${sheet}!${A1}]` for those).
  //   - `inputValues` ← `sandbox.overrides` → the cell the user typed into
  //     (`resolveValue` reads `inputValues[cell.namedRange]` DIRECTLY for
  //     `cellType === 'input'`, NOT `cellValues` — without this the edited
  //     input cell stays pinned to the frozen value; same fix as
  //     `<DealModelView>`, `deal-signal-overhaul` Phase 6 Session 5
  //     follow-up, 2026-05-12).
  // Memoized to keep <ModelLayout>'s `data` reference stable when neither
  // inputs nor overrides change.
  const mergedData = useMemo<ModelViewResponse>(
    () => ({
      ...data,
      cellValues: { ...data.cellValues, ...sandbox.recomputedValues },
      inputValues: { ...data.inputValues, ...sandbox.overrides },
    }),
    [data, sandbox.recomputedValues, sandbox.overrides],
  );

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden"
      data-testid="read-only-model-body"
      data-share-id={shareId}
    >
      <SandboxBanner
        shareMode="internal-recipient"
        hasOverrides={hasOverrides}
        onForkClick={onForkClick}
        className="mx-5 mt-3"
      />
      {sandbox.transientError && (
        <div
          data-testid="read-only-sandbox-transient-error"
          role="alert"
          className="mx-5 mt-2 px-3 py-1.5 text-xs font-mono text-warn bg-warn-dim border border-warn/15 rounded-[2px]"
        >
          {sandbox.transientError}
        </div>
      )}
      <ModelLayout
        data={mergedData}
        readOnly
        conversationId={null}
        onInputEdit={sandbox.applyEdit}
      />
    </div>
  );
}
